// Storico prezzi d'acquisto per articolo e fornitore — chi sta rincarando?
import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from "lucide-react";
import { wmsApi, fmtDate } from "./wmsApi";
import { WInput, WCard, WBadge, WToggle } from "./WmsUi";

const COLORS = ["#FF4500", "#0E7490", "#7C3AED", "#16A34A", "#DB2777", "#CA8A04", "#475569"];
const fmtP = (n) => (n == null ? "—" : n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const DeltaBadge = ({ pct, testId }) => {
  let el;
  if (pct == null) el = <WBadge tone="zinc"><Minus className="w-3 h-3 mr-1" /> unico</WBadge>;
  else if (pct > 0.05) el = <WBadge tone="red"><TrendingUp className="w-3 h-3 mr-1" /> +{fmtP(pct)}%</WBadge>;
  else if (pct < -0.05) el = <WBadge tone="green"><TrendingDown className="w-3 h-3 mr-1" /> {fmtP(pct)}%</WBadge>;
  else el = <WBadge tone="zinc"><Minus className="w-3 h-3 mr-1" /> stabile</WBadge>;
  return <span data-testid={testId}>{el}</span>;
};

const Spark = ({ points }) => {
  if (!points || points.length < 2) return <div className="w-24 h-8" />;
  const ps = points.map((x) => x.p);
  const min = Math.min(...ps), max = Math.max(...ps);
  const span = max - min || 1;
  const pts = ps.map((p, i) => `${(i / (ps.length - 1)) * 96 + 2},${30 - ((p - min) / span) * 26}`).join(" ");
  const up = ps[ps.length - 1] > ps[0];
  return (
    <svg width="100" height="32" className="shrink-0">
      <polyline points={pts} fill="none" stroke={up ? "#DC2626" : "#16A34A"} strokeWidth="2" />
    </svg>
  );
};

const Chart = ({ points }) => {
  const W = 720, H = 260, PX = 46, PY = 24;
  const suppliers = [...new Set(points.map((p) => p.supplier_name))];
  const dates = points.map((p) => new Date(p.date).getTime());
  const prices = points.map((p) => p.price);
  const minD = Math.min(...dates), maxD = Math.max(...dates);
  const minP = Math.min(...prices), maxP = Math.max(...prices);
  const spanD = maxD - minD || 1, spanP = maxP - minP || minP * 0.1 || 1;
  const x = (d) => PX + ((new Date(d).getTime() - minD) / spanD) * (W - PX - 14);
  const y = (p) => H - PY - ((p - minP) / spanP) * (H - PY * 2);
  return (
    <div className="overflow-x-auto" data-testid="price-chart">
      <svg width={W} height={H} className="bg-white border border-zinc-200">
        {[0, 0.5, 1].map((t) => {
          const p = minP + t * spanP;
          return (
            <g key={t}>
              <line x1={PX} y1={y(p)} x2={W - 14} y2={y(p)} stroke="#E4E4E7" strokeDasharray="3 3" />
              <text x={PX - 5} y={y(p) + 3} textAnchor="end" fontSize="10" fill="#71717A" fontFamily="monospace">{fmtP(p)}</text>
            </g>
          );
        })}
        {suppliers.map((s, si) => {
          const sp = points.filter((p) => p.supplier_name === s);
          const line = sp.map((p) => `${x(p.date)},${y(p.price)}`).join(" ");
          return (
            <g key={s}>
              {sp.length > 1 && <polyline points={line} fill="none" stroke={COLORS[si % COLORS.length]} strokeWidth="2.5" />}
              {sp.map((p, i) => (
                <circle key={i} cx={x(p.date)} cy={y(p.price)} r="4.5" fill={COLORS[si % COLORS.length]} stroke="#fff" strokeWidth="1.5">
                  <title>{`${s} — ${fmtDate(p.date)} — ${fmtP(p.price)} €/${p.price_um}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
        <text x={PX} y={H - 6} fontSize="10" fill="#71717A">{fmtDate(points[0].date)}</text>
        <text x={W - 14} y={H - 6} textAnchor="end" fontSize="10" fill="#71717A">{fmtDate(points[points.length - 1].date)}</text>
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {suppliers.map((s, si) => (
          <span key={s} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase text-zinc-600">
            <span className="w-3 h-3 inline-block" style={{ background: COLORS[si % COLORS.length] }} /> {s}
          </span>
        ))}
      </div>
    </div>
  );
};

const ProductDetail = ({ productId }) => {
  const [points, setPoints] = useState(null);
  useEffect(() => {
    wmsApi.get(`/wms/price-history/${productId}`).then((r) => setPoints(r.data.points)).catch(() => setPoints([]));
  }, [productId]);
  if (!points) return <p className="text-sm text-zinc-400 p-4">Caricamento…</p>;
  if (!points.length) return <p className="text-sm text-zinc-400 p-4">Nessun dato.</p>;
  return (
    <div className="border-t-2 border-zinc-200 p-4 space-y-4 bg-zinc-50" data-testid="price-detail">
      <Chart points={points} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="price-history-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b-2 border-zinc-300">
              <th className="py-2 pr-3">Data</th><th className="pr-3">Fornitore</th><th className="pr-3 text-right">Prezzo</th>
              <th className="pr-3">Lotto forn.</th><th className="pr-3">Bolla</th><th>Carico</th>
            </tr>
          </thead>
          <tbody>
            {[...points].reverse().map((p, i, arr) => {
              const older = arr[i + 1];
              const d = older && older.price ? ((p.price - older.price) / older.price) * 100 : null;
              return (
                <tr key={i} className="border-b border-zinc-200">
                  <td className="py-2 pr-3 font-mono">{fmtDate(p.date)}</td>
                  <td className="pr-3 font-bold">{p.supplier_name}</td>
                  <td className="pr-3 text-right font-mono font-bold whitespace-nowrap">
                    {fmtP(p.price)} €/{p.price_um}
                    {d != null && Math.abs(d) > 0.05 && (
                      <span className={`ml-2 text-[11px] ${d > 0 ? "text-red-600" : "text-green-700"}`}>{d > 0 ? "▲" : "▼"} {fmtP(Math.abs(d))}%</span>
                    )}
                  </td>
                  <td className="pr-3 font-mono text-zinc-500">{p.lot_supplier || "—"}</td>
                  <td className="pr-3 text-zinc-500">{p.ddt_number || "—"}</td>
                  <td className="text-zinc-500">{p.receipt_number}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function WmsPrices() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recenti");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    wmsApi.get("/wms/price-history").then((r) => setData(r.data.products)).catch(() => setData([]));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((x) => x.product_name.toLowerCase().includes(s) || x.product_code.toLowerCase().includes(s) || x.suppliers.some((f) => f.toLowerCase().includes(s)));
    }
    if (sort === "rincari") r = [...r].sort((a, b) => (b.delta_pct ?? -Infinity) - (a.delta_pct ?? -Infinity));
    else if (sort === "nome") r = [...r].sort((a, b) => a.product_name.localeCompare(b.product_name));
    return r;
  }, [data, q, sort]);

  return (
    <div className="space-y-5" data-testid="wms-prices-page">
      <div>
        <h1 className="font-['Chivo'] font-black text-3xl md:text-4xl tracking-tighter uppercase">Prezzi d'acquisto</h1>
        <p className="text-zinc-500">Andamento per articolo e fornitore — chi sta rincarando</p>
      </div>
      <div className="flex flex-col md:flex-row gap-3">
        <WInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca articolo, codice o fornitore…" className="md:max-w-md" data-testid="prices-search" />
        <WToggle
          options={[{ value: "recenti", label: "Recenti" }, { value: "rincari", label: "Rincari" }, { value: "nome", label: "Nome" }]}
          value={sort} onChange={setSort} testIdPrefix="prices-sort"
        />
      </div>

      {data === null && <p className="text-zinc-400">Caricamento…</p>}
      {data !== null && rows.length === 0 && (
        <WCard className="p-8 text-center text-zinc-400" data-testid="prices-empty">
          Nessun prezzo registrato: inserisci il prezzo d'acquisto negli articoli dei carichi e comparirà qui lo storico.
        </WCard>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <WCard key={r.product_id} className="border-2 border-zinc-300" data-testid={`price-row-${r.product_id}`}>
            <button
              className="w-full flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left hover:bg-zinc-50"
              onClick={() => setOpenId(openId === r.product_id ? null : r.product_id)}
              data-testid={`price-row-btn-${r.product_id}`}
            >
              <div className="flex-1 min-w-[200px]">
                <p className="font-['Chivo'] font-black uppercase leading-tight">{r.product_name}</p>
                <p className="text-xs text-zinc-500 font-mono">{r.product_code}{r.category_name ? ` · ${r.category_name}` : ""}</p>
              </div>
              <Spark points={r.spark} />
              <div className="text-right">
                <p className="font-mono font-black text-xl whitespace-nowrap">{fmtP(r.last.price)} <span className="text-xs text-zinc-500">€/{r.price_um}</span></p>
                <p className="text-[11px] text-zinc-500">{fmtDate(r.last.date)} · {r.last.supplier}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <DeltaBadge pct={r.delta_pct} testId={`price-delta-${r.product_id}`} />
                <p className="text-[11px] text-zinc-400">{r.purchases_count} acquisti · {r.suppliers.length} fornitor{r.suppliers.length === 1 ? "e" : "i"}</p>
              </div>
              {openId === r.product_id ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
            </button>
            {openId === r.product_id && <ProductDetail productId={r.product_id} />}
          </WCard>
        ))}
      </div>
    </div>
  );
}
